import {
  ReactNode,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { OrthographicCamera, Quaternion, Vector3 } from "three";
import { useCamera } from "../../contexts/camera";
import { useMeasureSize } from "./useMeasureSize";

import "./OrbitalButtons.css";

export type OrbitalButtonSpec = {
  content: ReactNode;
  vector: Vector3;
};

export type OrbitalButtonsProps = {
  buttons: OrbitalButtonSpec[];
};

export function OrbitalButtons(props: OrbitalButtonsProps) {
  const { buttons } = props;
  const [cameraCtx] = useCamera();
  const [containerRef, containerRect] = useMeasureSize();

  const projectorCamera = useMemo(() => new OrthographicCamera(
    -1, 1, -1, 1, -2, 2
  ), []);
  const [serializedQuat, setSerializedQuat] = useState<string>("");

  useLayoutEffect(() => {
    const updateFromCameraContext = () => {
      const { worldRotation } = cameraCtx;
      if (worldRotation === undefined) return;
      projectorCamera.setRotationFromQuaternion(worldRotation);
      projectorCamera.updateProjectionMatrix();
      projectorCamera.updateMatrixWorld();
      setSerializedQuat(serializeQuat(worldRotation));
    };
    cameraCtx.events.on("update", updateFromCameraContext);
    updateFromCameraContext();
    return () => {
      cameraCtx.events.off("update", updateFromCameraContext);
    }
  }, [projectorCamera, cameraCtx]);

  const depthSortedButtons = useMemo(() => {
    if (serializedQuat === "") return [];
    const projectedButtons = buttons.map((btn) => {
      const pos = btn.vector.clone().project(projectorCamera);
      return {
        btn,
        pos,
      };
    });
    projectedButtons.sort((a, b) => (b.pos?.z ?? 0) - (a.pos?.z ?? 0));
    return projectedButtons;
  }, [projectorCamera, buttons, serializedQuat]);

  return (
    <div className="orbital-buttons-container" ref={containerRef}>
      {depthSortedButtons.map((btn, z) => (
        <div
          key={z}
          className="orbital-button-container"
          style={{
            top: ((btn.pos?.y ?? 0) + 1) * 0.5 * containerRect.width,
            left: ((btn.pos?.x ?? 0) + 1) * 0.5 * containerRect.height,
          }}
        >
          {btn.btn.content}
        </div>
      ))}
    </div>
  );
}

function serializeQuat(quat: Quaternion): string {
  return quat
    .toArray()
    .map((n) => n.toPrecision(3))
    .join(",");
}
